import React, { useState, useEffect, useCallback } from "react";

import {
  listMqttBrokers,
  createMqttBroker,
  provisionHostedMqttBroker,
  updateMqttBroker,
  deleteMqttBroker,
  getMqttBrokerState,
  getMqttBrokerDeviceCredentials,
  updateMqttBrokerDeviceCredentials,
  listMqttSubscriptions,
  createMqttSubscription,
  updateMqttSubscription,
  deleteMqttSubscription,
  getFolderTree,
  listHistorianProviders,
} from "../../../../api";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import Select from "../../../../components/Select";
import NumberInput from "../../../../components/NumberInput";
import PasswordInput from "../../../../components/PasswordInput";
import FolderPicker from "../../../../components/FolderPicker";
import {
  DEADBAND_MODE_OPTIONS,
  INTERVAL_UNIT_OPTIONS,
  MIN_HISTORIAN_INTERVAL_MS,
  historianIntervalInputMin,
  historianIntervalToMs,
  isIntervalHistorianMode,
  msToInterval,
} from "./historian-defaults";
import plusIcon from "../../../../assets/plus.svg";
import xmarkIcon from "../../../../assets/xmark.svg";
import moreIcon from "../../../../assets/more-horiz.svg";

const POLL_INTERVAL = 10000;

const URL_SCHEMES = [
  { value: "mqtt", label: "mqtt://" },
  { value: "mqtts", label: "mqtts://" },
  { value: "tcp", label: "tcp://" },
  { value: "ssl", label: "ssl://" },
  { value: "ws", label: "ws://" },
  { value: "wss", label: "wss://" },
];

const QOS_OPTIONS = [
  { value: "0", label: "0 — at most once" },
  { value: "1", label: "1 — at least once" },
];

const MODE_OPTIONS = [
  { value: "sparkplug", label: "Sparkplug B" },
  { value: "topic-per-tag", label: "Topic-per-tag (auto-create)" },
  { value: "json-expand", label: "JSON-expand (one tag per field)" },
];

const SPARKPLUG_SCOPE_OPTIONS = [
  { value: "both", label: "Node and device metrics" },
  { value: "node", label: "Node metrics only" },
];

// Legacy "direct" mode is no longer creatable from this UI — per-tag MQTT
// sources in the Tag Editor already register the broker subscribe, so a
// Direct subscription record was a no-op (router_subs.go skips it). Existing
// rows are still allowed to round-trip through the edit modal so users can
// migrate them.
const LEGACY_DIRECT_OPTION = { value: "direct", label: "Direct (legacy — use a per-tag MQTT source instead)" };

// sparkplugSubscribeTopic mirrors core/drivers/mqtt/sparkplug.SubscribeTopic
// in JS. Empty group / edgeNode default to the single-level wildcard "+".
// Empty device emits a 4-segment node-only filter (NBIRTH/NDATA/NDEATH);
// non-empty device emits a 5-segment device-level filter
// (DBIRTH/DDATA/DDEATH). "+" for device matches any device.
const sparkplugSubscribeTopic = (group, edgeNode, device) => {
  const g = (group || "").trim() || "+";
  const e = (edgeNode || "").trim() || "+";
  const d = (device || "").trim();
  if (d === "") return `spBv1.0/${g}/+/${e}`;
  return `spBv1.0/${g}/+/${e}/${d}`;
};

// parseSparkplugSubscribeTopic is the inverse — accepts either the
// 4-segment node-only or 5-segment device-level filter shape and
// returns {group, edgeNode, device}. For group/edgeNode, "+" maps back
// to "" (the field's "match any" affordance). For device, the literal
// "+" is preserved: device="" means "4-segment node-only filter," so
// collapsing a wildcard "+" to "" would change the topic shape.
// Returns null if the topic doesn't match either canonical shape.
const parseSparkplugSubscribeTopic = (topic) => {
  if (!topic) return null;
  const parts = topic.split("/");
  if (parts.length !== 4 && parts.length !== 5) return null;
  if (parts[0] !== "spBv1.0") return null;
  if (parts[2] !== "+") return null;
  if (!parts[1] || !parts[3]) return null;
  if (parts.length === 5 && !parts[4]) return null;
  return {
    group: parts[1] === "+" ? "" : parts[1],
    edgeNode: parts[3] === "+" ? "" : parts[3],
    device: parts.length === 5 ? parts[4] : "",
  };
};

const VALUE_TYPE_OPTIONS = [
  { value: "auto", label: "auto-infer (per-topic)" },
  { value: "string", label: "string" },
  { value: "float64", label: "float64" },
  { value: "int64", label: "int64" },
  { value: "bool", label: "bool" },
  { value: "json", label: "json" },
];

const STATUS_COLORS = {
  online: { bg: "rgba(76,175,80,0.12)", color: "rgba(76,175,80,0.95)", border: "rgba(76,175,80,0.25)", dot: "#4caf50" },
  connecting: { bg: "rgba(255,193,7,0.12)", color: "rgba(255,193,7,0.95)", border: "rgba(255,193,7,0.25)", dot: "#ffc107" },
  offline: { bg: "rgba(158,158,158,0.12)", color: "rgba(200,200,200,0.95)", border: "rgba(158,158,158,0.25)", dot: "#9e9e9e" },
  error: { bg: "rgba(244,67,54,0.12)", color: "rgba(244,67,54,0.95)", border: "rgba(244,67,54,0.25)", dot: "#f44336" },
};

const StatusDot = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.offline;
  return (
    <span title={status} style={{ width: 8, height: 8, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
  );
};

const errorMessage = (err, fb) => {
  const m = String(err?.message || err).trim();
  return m || fb;
};

// ── Broker create/edit modal ────────────────────────────────────────

// Splits a stored URL like "mqtt://host:1883" into {scheme, hostPort} so
// the form can render scheme as a dropdown alongside a plain host:port
// input. The two are recombined on submit.
const splitUrl = (raw) => {
  if (!raw) return { scheme: "mqtt", hostPort: "" };
  const idx = raw.indexOf("://");
  if (idx <= 0) return { scheme: "mqtt", hostPort: raw };
  return { scheme: raw.slice(0, idx).toLowerCase(), hostPort: raw.slice(idx + 3) };
};

const INITIAL_BROKER = {
  name: "", scheme: "mqtt", hostPort: "", clientId: "",
  username: "", password: "",
  keepAliveSec: "60", cleanStart: true,
  primaryHostId: "",
  tlsEnabled: false, tlsSkipVerify: false,
  tlsCaPem: "", tlsClientCertPem: "", tlsClientKey: "",
};

const BrokerModal = ({ editing, prefilled, onClose, onSaved }) => {
  const [form, setForm] = useState(() => {
    if (editing) {
      const { scheme, hostPort } = splitUrl(editing.url);
      return {
        name: editing.name || "",
        scheme, hostPort,
        clientId: editing.client_id || "",
        username: editing.username || "",
        password: "", // never returned by API
        keepAliveSec: String(editing.keep_alive_sec || 60),
        primaryHostId: editing.primary_host_id || "",
        cleanStart: editing.clean_start !== false,
        tlsEnabled: !!editing.tls_enabled,
        tlsSkipVerify: !!editing.tls_skip_verify,
        tlsCaPem: editing.tls_ca_pem || "",
        tlsClientCertPem: editing.tls_client_cert_pem || "",
        tlsClientKey: "", // never returned by API
      };
    }
    if (prefilled) {
      const port = prefilled.port ? `:${prefilled.port}` : "";
      return {
        ...INITIAL_BROKER,
        name: prefilled.name || "",
        scheme: prefilled.tls ? "mqtts" : "mqtt",
        hostPort: prefilled.host ? `${prefilled.host}${port}` : "",
        clientId: prefilled.client_id || "",
        username: prefilled.username || "",
        keepAliveSec: prefilled.keepalive_seconds != null
          ? String(prefilled.keepalive_seconds)
          : INITIAL_BROKER.keepAliveSec,
        tlsEnabled: !!prefilled.tls,
      };
    }
    return INITIAL_BROKER;
  });
  const [submitting, setSubmitting] = useState(false);
  const notify = useNotify();
  const isEdit = !!editing;

  const set = useCallback((field) => (e) => {
    const value = typeof e === "string" ? e : (e?.target?.type === "checkbox" ? e.target.checked : e.target.value);
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Backend treats omitted fields on PATCH as "leave unchanged" for
    // sensitive material, and empty strings as "clear". Mirror that on
    // edit: skip password / TLS key when blank so existing secrets stay.
    const payload = {
      name: form.name,
      url: `${form.scheme}://${form.hostPort}`,
      client_id: form.clientId,
      username: form.username,
      keep_alive_sec: parseInt(form.keepAliveSec, 10) || 60,
      primary_host_id: form.primaryHostId.trim(),
      clean_start: form.cleanStart,
      tls_enabled: form.tlsEnabled,
      tls_skip_verify: form.tlsSkipVerify,
      tls_ca_pem: form.tlsCaPem,
      tls_client_cert_pem: form.tlsClientCertPem,
    };

    if (isEdit) {
      // PATCH semantics: only send password/key when the user actually
      // typed one. Empty string would clear the existing secret.
      if (form.password) payload.password = form.password;
      if (form.tlsClientKey) payload.tls_client_key = form.tlsClientKey;
    } else {
      payload.password = form.password;
      payload.tls_client_key = form.tlsClientKey;
    }

    try {
      if (isEdit) {
        await updateMqttBroker(editing.id, payload);
      } else {
        await createMqttBroker(payload);
      }
      notify({ type: "success", title: isEdit ? "Updated" : "Created", message: `Broker "${form.name || form.hostPort}" ${isEdit ? "updated" : "created"}.` });
      onSaved();
      onClose();
    } catch (err) {
      notify({ type: "error", title: isEdit ? "Update Failed" : "Create Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form className="gwModal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} style={{ width: 560 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">{isEdit ? `Edit: ${editing.name || editing.id}` : "New MQTT Broker"}</div>
            <div className="gwModalSubtitle">{isEdit ? "Update the broker configuration." : "Configure a new MQTT broker connection."}</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          <div className="gwField">
            <label className="gwLabel">Name</label>
            <input className="gwInput" placeholder="e.g. plant-floor-mqtt" value={form.name} onChange={set("name")} autoFocus={!isEdit} />
            <div className="gwHint">Display name. Must be unique (case-insensitive) if set.</div>
          </div>
          <div className="gwFieldRow">
            <div className="gwField" style={{ flex: 1 }}>
              <label className="gwLabel">Scheme</label>
              <Select value={form.scheme} onChange={set("scheme")} options={URL_SCHEMES} />
            </div>
            <div className="gwField" style={{ flex: 3 }}>
              <label className="gwLabel">Host : Port</label>
              <input className="gwInput" placeholder="e.g. broker.example.com:1883" value={form.hostPort} onChange={set("hostPort")} required autoFocus={isEdit} />
            </div>
          </div>
          <div className="gwFieldRow">
            <div className="gwField">
              <label className="gwLabel">Client ID</label>
              <input className="gwInput" placeholder="auto-generated if blank" value={form.clientId} onChange={set("clientId")} />
            </div>
            <div className="gwField">
              <label className="gwLabel">Keep-Alive (sec)</label>
              <NumberInput value={form.keepAliveSec} onChange={set("keepAliveSec")} min={1} max={3600} />
            </div>
          </div>
          <div className="gwField">
            <label className="gwLabel">Primary Host ID</label>
            <input className="gwInput" placeholder="(optional)" value={form.primaryHostId} onChange={set("primaryHostId")} />
            <div className="gwHint">Optional. Sparkplug primary host id — edge nodes configured to wait for this host will only publish once Control Seat announces itself online. Leave blank to act as a passive subscriber.</div>
          </div>
          <div className="gwFieldRow">
            <div className="gwField">
              <label className="gwLabel">Username</label>
              <input className="gwInput" placeholder="(optional)" value={form.username} onChange={set("username")} />
            </div>
            <div className="gwField">
              <label className="gwLabel">Password</label>
              <PasswordInput placeholder={isEdit ? "Leave blank to keep current" : "(optional)"} value={form.password} onChange={set("password")} />
            </div>
          </div>
          <div className="gwField">
            <label className="gwLabel" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={form.cleanStart} onChange={set("cleanStart")} />
              <span>Clean Start</span>
            </label>
            <div className="gwHint">When enabled, the broker discards prior session state on each connect (recommended for v1).</div>
          </div>

          <div className="gwField">
            <label className="gwLabel" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={form.tlsEnabled} onChange={set("tlsEnabled")} />
              <span>Enable TLS</span>
            </label>
          </div>
          {form.tlsEnabled && (
            <>
              <div className="gwField">
                <label className="gwLabel" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.tlsSkipVerify} onChange={set("tlsSkipVerify")} />
                  <span>Skip Certificate Verification</span>
                </label>
                <div className="gwHint">Dev only — never enable against production brokers.</div>
              </div>
              <div className="gwField">
                <label className="gwLabel">Custom CA (PEM)</label>
                <textarea className="gwInput" rows={3} placeholder="-----BEGIN CERTIFICATE-----" value={form.tlsCaPem} onChange={set("tlsCaPem")} style={{ fontFamily: "monospace", fontSize: 11 }} />
              </div>
              <div className="gwField">
                <label className="gwLabel">Client Certificate (PEM, mTLS)</label>
                <textarea className="gwInput" rows={3} placeholder="-----BEGIN CERTIFICATE-----" value={form.tlsClientCertPem} onChange={set("tlsClientCertPem")} style={{ fontFamily: "monospace", fontSize: 11 }} />
              </div>
              <div className="gwField">
                <label className="gwLabel">Client Private Key (mTLS)</label>
                <PasswordInput placeholder={isEdit ? "Leave blank to keep current" : "-----BEGIN PRIVATE KEY-----"} value={form.tlsClientKey} onChange={set("tlsClientKey")} />
                <div className="gwHint">Stored encrypted via the secret store. Cert and key must be supplied as a pair.</div>
              </div>
            </>
          )}
        </div>
        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          <button className="gwPrimaryBtn" type="submit" disabled={submitting}>
            {submitting ? "Saving..." : isEdit ? "Save" : "Create Broker"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Subscription create/edit modal ──────────────────────────────────

const INITIAL_SUB = {
  topic: "", qos: "1", mode: "sparkplug", valueType: "string", valuePath: "",
  folderId: "", // empty = root (sent as omitted)
  // sparkplug only; ignored for other modes. scope is "node" (4-segment
  // node-only filter) or "both" (5-segment with `#` capturing node and
  // device messages in one subscription).
  scope: "both", group: "", edgeNode: "",
  // Writable defaults off: auto-imported MQTT tags are read-only unless the
  // subscription explicitly opts in. PATCH propagates the new value to every
  // existing owned tag of the subscription.
  writable: false,
  // Historian defaults applied to this subscription's auto-created tags.
  historianEnabled: false,
  historianMode: "0",
  historianIntervalValue: "5",
  historianIntervalUnit: "s",
  deadbandMode: "0",
  deadbandValue: "0",
  historianProviderId: "", // empty = default local historian
};

// Maps a parsed device segment to the selected scope. Anything non-empty
// (legacy concrete device, `+`, `#`) collapses to "both" — the form
// re-emits as `#` on save, normalizing legacy device-level subs to the
// shape the dispatcher can actually serve.
const scopeFromDevice = (device) => (device === "" ? "node" : "both");

// Resolves the device segment to send based on the chosen scope.
const deviceForScope = (scope) => (scope === "node" ? "" : "#");

const SubscriptionModal = ({ brokerId, editing, onClose, onSaved }) => {
  const [form, setForm] = useState(() => {
    if (!editing) return INITIAL_SUB;
    // For sparkplug subs, parse the stored filter back into Group +
    // Edge Node so the edit form shows the original IDs instead of
    // the synthesized "spBv1.0/.../+/..." string. Falls back to
    // empties (treated as wildcard "+") if the topic doesn't match
    // the canonical filter shape.
    const parsed = editing.mode === "sparkplug"
      ? parseSparkplugSubscribeTopic(editing.topic) || { group: "", edgeNode: "", device: "" }
      : { group: "", edgeNode: "", device: "" };
    const historianMode = String(editing.historian_mode || 0);
    const historianInterval = msToInterval(
      isIntervalHistorianMode(historianMode)
        ? Math.max(editing.historian_interval_ms || 0, MIN_HISTORIAN_INTERVAL_MS)
        : editing.historian_interval_ms || 0
    );
    return {
      topic: editing.topic || "",
      qos: String(editing.qos || 0),
      mode: editing.mode || "sparkplug",
      valueType: editing.value_type || "string",
      valuePath: editing.value_path || "",
      folderId: editing.folder_id || "",
      scope: scopeFromDevice(parsed.device),
      group: parsed.group,
      edgeNode: parsed.edgeNode,
      writable: !!editing.writable,
      historianEnabled: !!editing.historian_enabled,
      historianMode,
      historianIntervalValue: historianInterval.value,
      historianIntervalUnit: historianInterval.unit,
      deadbandMode: String(editing.deadband_mode || 0),
      deadbandValue: String(editing.deadband_value || 0),
      historianProviderId: editing.historian_provider_id || "",
    };
  });
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [providers, setProviders] = useState([]);
  const notify = useNotify();
  const isEdit = !!editing;

  // Historian providers for the storage-target dropdown. Tolerates a failed
  // fetch by leaving the list empty — the built-in local historian still shows.
  useEffect(() => {
    let alive = true;
    listHistorianProviders()
      .then((list) => { if (alive) setProviders(list || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // The local built-in's label comes from the API (build-accurate name);
  // other providers list by name. Empty value = default local historian.
  const defaultLocal = providers.find((p) => p && p.id === "default_local");
  const providerOptions = [
    { value: "", label: defaultLocal?.name || "Control Seat Local Historian" },
    ...providers
      .filter((p) => p && p.id && p.id !== "default_local")
      .map((p) => ({ value: p.id, label: p.name || p.id })),
  ];

  const set = useCallback((field) => (e) => {
    const value = typeof e === "string" ? e : (e?.target?.type === "checkbox" ? e.target.checked : e.target.value);
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const isSparkplug = form.mode === "sparkplug";
    // json-expand writes return ErrWriteUnsupportedMode regardless of the
    // flag, so always persist false for that mode to avoid the UI implying
    // a write path that the backend can't honor. The toggle reflects this
    // by disabling for json-expand.
    const writable = form.mode === "json-expand" ? false : form.writable;
    // Sparkplug subscriptions persist a synthesized canonical filter —
    // 4-segment node-only when device is blank, 5-segment device-level
    // otherwise. value_type / value_path are not meaningful here:
    // per-metric types come from the protobuf payload, and the backend's
    // Subscription.Validate rejects anything non-empty. Build the filter
    // from the Group + Edge Node + Device fields and clear the others.
    const topic = isSparkplug
      ? sparkplugSubscribeTopic(form.group, form.edgeNode, deviceForScope(form.scope))
      : form.topic;
    const historianEnabled = form.mode !== "direct" && form.historianEnabled;

    const payload = {
      topic,
      qos: parseInt(form.qos, 10) || 0,
      mode: form.mode,
      value_type: isSparkplug ? "" : form.valueType,
      value_path: isSparkplug ? "" : form.valuePath,
      // Always send folder_id. Backend normalizes "" to nil (root) on
      // both Create and Update, so explicitly clearing the picker via
      // "(root)" actually moves the subscription back to root instead
      // of being silently dropped from the payload.
      folder_id: form.folderId || "",
      // Seeds writable onto newly auto-created tags. Existing owned tags keep
      // their per-tag writable overrides.
      writable,
      // Historian defaults seeded onto this subscription's auto-created tags.
      // Always sent so disabling (or changing) re-applies to existing tags.
      // Interval is only meaningful for interval/both modes — send 0 otherwise
      // so an on-change subscription round-trips stably (no spurious re-apply
      // over its tags on an unrelated edit). Direct mode auto-creates no tags,
      // so never persist a (dead) historian default for it.
      historian_enabled: historianEnabled,
      historian_mode: parseInt(form.historianMode, 10) || 0,
      historian_interval_ms:
        isIntervalHistorianMode(form.historianMode)
          ? historianIntervalToMs(form.historianIntervalValue, form.historianIntervalUnit)
          : 0,
      deadband_mode: parseInt(form.deadbandMode, 10) || 0,
      deadband_value: parseFloat(form.deadbandValue) || 0,
    };

    // Where this subscription's tags store history. Omit for disabled
    // historian and for create-on-built-in; the backend defaults to local.
    // On edit, still send "" when clearing a previously external provider.
    if (historianEnabled && (form.historianProviderId || (isEdit && editing?.historian_provider_id))) {
      payload.historian_provider_id = form.historianProviderId || "";
    }

    try {
      if (isEdit) {
        await updateMqttSubscription(editing.id, payload);
      } else {
        await createMqttSubscription(brokerId, payload);
      }
      notify({ type: "success", title: isEdit ? "Updated" : "Created", message: `Subscription ${isEdit ? "updated" : "created"}.` });
      onSaved();
      onClose();
    } catch (err) {
      notify({ type: "error", title: isEdit ? "Update Failed" : "Create Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setSubmitting(false);
    }
  };

  // Historian gets its own tab (mirrors the tag editor's Historian tab). Direct
  // mode auto-creates no tags, so the tab — and the historian default — don't
  // apply; hide it and pin the modal to General.
  const showHistorian = form.mode !== "direct";
  const effectiveTab = showHistorian && activeTab === "historian" ? "historian" : "general";
  const historianOnChange = form.historianMode === "0" || form.historianMode === "2";
  const historianInterval = form.historianMode === "1" || form.historianMode === "2";
  const modeDescription =
    form.mode === "direct"
      ? "Legacy direct mode — this record is a no-op at runtime. Configure the broker, topic, and value type on the tag itself via its MQTT source."
      : form.mode === "sparkplug"
        ? "Sparkplug B: per-metric tags auto-created from NBIRTH/DBIRTH; alias map maintained per edge node and per device."
        : "Wildcard topics auto-create tags as messages arrive.";

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form className="gwModal gwSubscriptionModal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <div className="gwModalHeader">
          <div className="gwModalTitle">{isEdit ? "Edit Subscription" : "New Subscription"}</div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          {showHistorian && (
            <div className="gwSubscriptionTabs">
              <button
                type="button"
                className={`gwSubscriptionTab ${effectiveTab === "general" ? "isActive" : ""}`}
                onClick={() => setActiveTab("general")}
              >
                General
              </button>
              <button
                type="button"
                className={`gwSubscriptionTab ${effectiveTab === "historian" ? "isActive" : ""}`}
                onClick={() => setActiveTab("historian")}
              >
                Historian
              </button>
            </div>
          )}
          {effectiveTab === "general" && (
            <div className="gwSubscriptionTabPanel">
              <div className="gwField">
                <label className="gwLabel">Mode</label>
                <Select
                  value={form.mode}
                  onChange={set("mode")}
                  options={form.mode === "direct" ? [LEGACY_DIRECT_OPTION, ...MODE_OPTIONS] : MODE_OPTIONS}
                />
                <div className="gwHint gwSubscriptionModeHint">{modeDescription}</div>
              </div>
          {form.mode === "sparkplug" ? (
            <>
              <div className="gwFieldRow">
                <div className="gwField">
                  <label className="gwLabel">Group ID</label>
                  <input
                    className="gwInput"
                    placeholder="Plant1 (blank = all groups)"
                    value={form.group}
                    onChange={set("group")}
                  />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Edge Node ID</label>
                  <input
                    className="gwInput"
                    placeholder="EON1 (blank = all nodes)"
                    value={form.edgeNode}
                    onChange={set("edgeNode")}
                  />
                </div>
              </div>
              <div className="gwField">
                <label className="gwLabel">Scope</label>
                <Select value={form.scope} onChange={set("scope")} options={SPARKPLUG_SCOPE_OPTIONS} />
                <div className="gwHint">
                  {form.scope === "node"
                    ? "Includes NBIRTH, NDATA, and NDEATH metrics."
                    : "Includes node metrics and metrics published by its devices."}
                </div>
              </div>
              <div className="gwField">
                <div className="gwHint">
                  Subscribes to{" "}
                  <code style={{ fontFamily: "monospace" }}>
                    {sparkplugSubscribeTopic(form.group, form.edgeNode, deviceForScope(form.scope))}
                  </code>
                  . Per-metric tags are auto-created under the folder below using
                  <code style={{ fontFamily: "monospace" }}>
                    {form.scope === "both"
                      ? " <group>/<edge>/[<device>/]<metric>"
                      : " <group>/<edge>/<metric>"}
                  </code>.
                </div>
              </div>
              <div className="gwField">
                <label className="gwLabel">QoS</label>
                <Select value={form.qos} onChange={set("qos")} options={QOS_OPTIONS} />
              </div>
            </>
          ) : (
            <>
              <div className="gwField">
                <label className="gwLabel">Topic Filter</label>
                <input
                  className="gwInput"
                  placeholder={form.mode === "direct" ? "sensors/pump1/temp" : "sensors/+/temp"}
                  value={form.topic}
                  onChange={set("topic")}
                  required
                />
                <div className="gwHint">
                  {form.mode === "direct"
                    ? "Literal topic; wildcards are not allowed in direct mode."
                    : "Use + to match a single level, # to match all trailing levels."}
                </div>
              </div>
              <div className="gwFieldRow">
                <div className="gwField">
                  <label className="gwLabel">QoS</label>
                  <Select value={form.qos} onChange={set("qos")} options={QOS_OPTIONS} />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Value Type</label>
                  <Select value={form.valueType} onChange={set("valueType")} options={VALUE_TYPE_OPTIONS} />
                </div>
              </div>
              {form.valueType === "json" && form.mode !== "json-expand" && (
                <div className="gwField">
                  <label className="gwLabel">Value Path (optional)</label>
                  <input className="gwInput" placeholder="data.temperature" value={form.valuePath} onChange={set("valuePath")} />
                  <div className="gwHint">Dot-separated path into the JSON payload. Leave blank to store the entire payload.</div>
                </div>
              )}
            </>
          )}
          {form.mode !== "direct" && (
            <div className="gwField">
              <label className="gwLabel">Folder (optional)</label>
              <FolderPicker
                value={form.folderId}
                onChange={(folderId) => setForm((prev) => ({ ...prev, folderId }))}
                placeholder="(root)"
              />
              <div className="gwHint">Auto-created tags will be placed under this folder. Leave blank for root.</div>
            </div>
          )}
          {form.mode !== "direct" && (() => {
            // json-expand writes return ErrWriteUnsupportedMode regardless of
            // the flag; disable + explain rather than letting the toggle imply
            // a path the backend won't take.
            const writeUnsupported = form.mode === "json-expand";
            const isOn = !writeUnsupported && form.writable;
            return (
              <div className="gwField">
                <label
                  className="gwHistToggle"
                  data-gw-field="writable"
                  style={{
                    cursor: writeUnsupported ? "not-allowed" : "pointer",
                    opacity: writeUnsupported ? 0.6 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    className="gwHistToggleInput"
                    checked={isOn}
                    onChange={set("writable")}
                    disabled={writeUnsupported}
                  />
                  <span className={`gwToggle ${isOn ? "isOn" : ""}`} aria-hidden="true"><span /></span>
                  Writable
                </label>
                <div className="gwHint">
                  {writeUnsupported
                    ? "Writes are not supported for json-expand subscriptions in this version."
                    : "Allow writing values back to these tags. Off keeps them read-only."}
                </div>
              </div>
            );
          })()}
            </div>
          )}
          {effectiveTab === "historian" && (
            <div className="gwSubscriptionTabPanel">
              <div className="gwField">
                <label className="gwHistToggle">
                  <input
                    type="checkbox"
                    className="gwHistToggleInput"
                    checked={form.historianEnabled}
                    onChange={set("historianEnabled")}
                  />
                  <span className={`gwToggle ${form.historianEnabled ? "isOn" : ""}`} aria-hidden="true"><span /></span>
                  Enable historian
                </label>
                <div className="gwHint">
                  Records this subscription's tags to the historian. Applies to existing and future tags; changing it re-applies to all of the subscription's tags.
                </div>
              </div>
              {form.historianEnabled && (
                <div className="gwField">
                  <label className="gwLabel">Historian provider</label>
                  <Select value={form.historianProviderId} onChange={set("historianProviderId")} options={providerOptions} />
                  <div className="gwHint">Where this subscription's tags store history. Applies to existing and future tags.</div>
                </div>
              )}

              {form.historianEnabled && (
                <div className="gwField">
                  <div className="gwHistSection">Recording options</div>
                  <div className="gwHistSectionBody">
                    <label className="gwHistToggle">
                      <input
                        type="checkbox"
                        className="gwHistToggleInput"
                        checked={historianOnChange}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setForm((p) => ({ ...p, historianMode: on ? (historianInterval ? "2" : "0") : "1" }));
                        }}
                      />
                      <span className={`gwToggle ${historianOnChange ? "isOn" : ""}`} aria-hidden="true"><span /></span>
                      Record on change
                    </label>

                    {historianOnChange && (
                      <div className="gwHistIndent">
                        <div className="gwField">
                          <label className="gwLabel">Deadband</label>
                          <Select value={form.deadbandMode} onChange={set("deadbandMode")} options={DEADBAND_MODE_OPTIONS} />
                        </div>
                        {form.deadbandMode !== "0" && (
                          <div className="gwField">
                            <label className="gwLabel">{form.deadbandMode === "2" ? "Value (%)" : "Value"}</label>
                            <NumberInput min={0} step={1} value={form.deadbandValue} onChange={set("deadbandValue")} placeholder="0" />
                          </div>
                        )}
                        <div className="gwHint">Deadband applies to numeric tags only; non-numeric tags are historized without it.</div>
                      </div>
                    )}

                    <label className="gwHistToggle">
                      <input
                        type="checkbox"
                        className="gwHistToggleInput"
                        checked={historianInterval}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setForm((p) => ({ ...p, historianMode: on ? (historianOnChange ? "2" : "1") : "0" }));
                        }}
                      />
                      <span className={`gwToggle ${historianInterval ? "isOn" : ""}`} aria-hidden="true"><span /></span>
                      Force interval recording
                    </label>

                    {historianInterval && (
                      <div style={{ display: "flex", gap: 12 }}>
                        <div className="gwField" style={{ flex: 1 }}>
                          <label className="gwLabel">Interval</label>
                          <NumberInput
                            min={historianIntervalInputMin(form.historianIntervalUnit)}
                            step={1}
                            value={form.historianIntervalValue}
                            onChange={set("historianIntervalValue")}
                            placeholder="100"
                          />
                        </div>
                        <div className="gwField" style={{ flexShrink: 0 }}>
                          <label className="gwLabel">Unit</label>
                          <Select value={form.historianIntervalUnit} onChange={set("historianIntervalUnit")} options={INTERVAL_UNIT_OPTIONS} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          <button className="gwPrimaryBtn" type="submit" disabled={submitting}>
            {submitting ? "Saving..." : isEdit ? "Save" : "Create Subscription"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Subscription panel (inline under broker row) ────────────────────

const SubscriptionsPanel = ({ brokerId, canConfigure }) => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Folder-id → name map for the card display. Lazy-loaded once when
  // the panel mounts; refreshed when subs change so a folder created
  // alongside a subscription resolves on the next render.
  const [folderNames, setFolderNames] = useState({});
  const notify = useNotify();

  const fetchSubs = useCallback(async () => {
    try {
      const list = await listMqttSubscriptions(brokerId);
      setSubs(list || []);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, "Failed to load subscriptions"));
    } finally {
      setLoading(false);
    }
  }, [brokerId]);

  useEffect(() => {
    fetchSubs();
  }, [fetchSubs]);

  // Load the folder name map. Best-effort: a failure leaves the cards
  // showing the raw folder ID rather than blocking the whole panel.
  useEffect(() => {
    getFolderTree({ lite: true, countsOnly: true })
      .then((data) => {
        const map = {};
        for (const b of data?.browsers || []) {
          if (b && b.id && b.id !== "__root__") map[b.id] = b.name || b.id;
        }
        setFolderNames(map);
      })
      .catch(() => {
        /* keep raw IDs in display */
      });
  }, [subs]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteMqttSubscription(deleteTarget.id);
      notify({ type: "success", title: "Removed", message: "Subscription deleted." });
      setDeleteTarget(null);
      fetchSubs();
    } catch (err) {
      notify({ type: "error", title: "Delete Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="gwSchemaPanel gwBrokerSubscriptions">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div className="gwSectionTitle" style={{ fontSize: 12 }}>Subscriptions</div>
        <button
          className="gwSecondaryBtn"
          type="button"
          onClick={() => setShowCreate(true)}
          disabled={!canConfigure}
          title={canConfigure ? undefined : "Requires configure permission"}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <img src={plusIcon} alt="" className="gwIcon" style={{ width: 12, height: 12 }} />
          <span>Add Subscription</span>
        </button>
      </div>
      {loading && <div className="gwSchemaHint">Loading subscriptions...</div>}
      {error && <div className="gwSchemaError">{error}</div>}
      {!loading && !error && subs.length === 0 && (
        <div className="gwSchemaHint">No subscriptions yet.</div>
      )}
      {!loading && !error && subs.length > 0 && (
        <div className="gwSubscriptionList">
          {subs.map((s) => (
            <div
              key={s.id}
              className="gwSubscriptionRow"
              data-gw-resource="mqtt_subscriptions"
              data-gw-id={s.id}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600 }}>{s.topic}</div>
                <div style={{ fontSize: 11, color: "var(--gw-muted)", marginTop: 2 }}>
                  {s.mode} &middot; QoS {s.qos} &middot; {s.value_type}
                  {s.value_path ? ` (${s.value_path})` : ""}
                  {s.folder_id ? ` · folder: ${folderNames[s.folder_id] || s.folder_id}` : ""}
                </div>
              </div>
              <button
                className="gwGhostBtn"
                type="button"
                onClick={() => setEditing(s)}
                disabled={!canConfigure}
                style={{ padding: "4px 10px", fontSize: 11 }}
              >
                Edit
              </button>
              <button
                className="gwGhostBtn"
                type="button"
                onClick={() => setDeleteTarget(s)}
                disabled={!canConfigure}
                style={{ padding: "4px 10px", fontSize: 11, color: "var(--gw-danger)" }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <SubscriptionModal
          brokerId={brokerId}
          onClose={() => setShowCreate(false)}
          onSaved={fetchSubs}
        />
      )}
      {editing && (
        <SubscriptionModal
          brokerId={brokerId}
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={fetchSubs}
        />
      )}
      {deleteTarget && (
        <div className="gwOverlay" onClick={() => setDeleteTarget(null)}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 400 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Remove Subscription</div>
                <div className="gwModalSubtitle">Auto-created tags from this subscription will be removed.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setDeleteTarget(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                Remove the subscription on <code>{deleteTarget.topic}</code>?
              </p>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="gwDangerBtn" type="button" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Page ────────────────────────────────────────────────────────────

// HostedConnectionPanel shows the device connection details for a Control
// Seat-managed broker: the public endpoint and username (always visible) plus
// the device password behind an explicit reveal (fetched on demand from the
// device-credentials endpoint). Each value has a copy button. This is what the
// customer pastes into their field devices.
const HostedConnectionPanel = ({ broker, canConfigure, onUpdated }) => {
  const notify = useNotify();
  const [endpoint, setEndpoint] = useState(broker.public_url || "");
  const [username, setUsername] = useState(broker.username || "");
  const [password, setPassword] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formUsername, setFormUsername] = useState(broker.username || "");
  const [formPassword, setFormPassword] = useState("");
  const [rotatePassword, setRotatePassword] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    setEndpoint(broker.public_url || "");
    setUsername(broker.username || "");
  }, [broker.public_url, broker.username]);

  const copy = async (label, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      notify({ type: "success", title: "Copied", message: `${label} copied to clipboard.` });
    } catch {
      notify({ type: "error", title: "Copy Failed", message: "Could not copy to clipboard." });
    }
  };

  const toggleReveal = async () => {
    if (revealed) {
      setRevealed(false);
      return;
    }
    if (password != null) {
      setRevealed(true);
      return;
    }
    setLoading(true);
    try {
      const creds = await getMqttBrokerDeviceCredentials(broker.id);
      setPassword(creds?.password || "");
      setRevealed(true);
    } catch (err) {
      notify({ type: "error", title: "Error", message: errorMessage(err, "Could not load device credentials") });
    } finally {
      setLoading(false);
    }
  };

  const openManage = () => {
    setFormUsername(username || "");
    setFormPassword("");
    setRotatePassword(false);
    setAcknowledged(false);
    setShowManage(true);
  };

  const closeManage = () => {
    if (saving) return;
    setShowManage(false);
  };

  const saveCredentials = async (e) => {
    e.preventDefault();
    const nextUsername = formUsername.trim();
    const nextPassword = formPassword;
    const payload = {};
    if (nextUsername !== username) payload.username = nextUsername;
    if (rotatePassword) {
      payload.rotate_password = true;
    } else if (nextPassword !== "") {
      payload.password = nextPassword;
    }
    if (Object.keys(payload).length === 0) {
      notify({ type: "error", title: "No Changes", message: "Change the username, set a password, or rotate the password." });
      return;
    }
    setSaving(true);
    try {
      const creds = await updateMqttBrokerDeviceCredentials(broker.id, payload);
      setEndpoint(creds?.public_url || endpoint);
      setUsername(creds?.username || nextUsername);
      setPassword(creds?.password || "");
      setRevealed(true);
      setShowManage(false);
      notify({
        type: "success",
        title: creds?.restarting ? "Credentials Updating" : "Credentials Updated",
        message: creds?.message || "Hosted MQTT device credentials updated.",
      });
      onUpdated?.();
    } catch (err) {
      notify({ type: "error", title: "Update Failed", message: errorMessage(err, "Could not update device credentials") });
    } finally {
      setSaving(false);
    }
  };

  const Row = ({ label, value, mono, action }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
      <span style={{ width: 84, flexShrink: 0, fontSize: 12, color: "var(--gw-muted)" }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontFamily: mono ? "var(--gw-mono, monospace)" : undefined, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value || <span style={{ color: "var(--gw-muted)" }}>—</span>}
      </span>
      {action}
    </div>
  );

  return (
    <div className="gwHostedConnPanel" style={{ marginTop: 10, padding: 12, borderRadius: 8, background: "var(--gw-surface-2, rgba(127,127,127,0.06))", border: "1px solid var(--gw-border)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 600 }}>Device connection</div>
        <button
          type="button"
          className="gwGhostBtn"
          style={{ padding: "2px 8px", fontSize: 12 }}
          onClick={openManage}
          disabled={!canConfigure}
          title={canConfigure ? undefined : "Requires configure permission"}
        >
          Manage
        </button>
      </div>
      <Row
        label="Endpoint"
        value={endpoint}
        mono
        action={<button type="button" className="gwGhostBtn" style={{ padding: "2px 8px", fontSize: 12 }} onClick={() => copy("Endpoint", endpoint)}>Copy</button>}
      />
      <Row
        label="Username"
        value={username}
        mono
        action={<button type="button" className="gwGhostBtn" style={{ padding: "2px 8px", fontSize: 12 }} onClick={() => copy("Username", username)}>Copy</button>}
      />
      <Row
        label="Password"
        value={revealed ? (password || "") : "••••••••••"}
        mono
        action={
          <span style={{ display: "inline-flex", gap: 6 }}>
            <button type="button" className="gwGhostBtn" style={{ padding: "2px 8px", fontSize: 12 }} onClick={toggleReveal} disabled={loading}>
              {loading ? "…" : revealed ? "Hide" : "Show"}
            </button>
            {revealed && (
              <button type="button" className="gwGhostBtn" style={{ padding: "2px 8px", fontSize: 12 }} onClick={() => copy("Password", password || "")}>Copy</button>
            )}
          </span>
        }
      />
      <div style={{ marginTop: 6, fontSize: 11, color: "var(--gw-muted)" }}>
        Point your MQTT devices at this endpoint with these credentials. Published topics become tags automatically.
      </div>
      {showManage && (
        <div className="gwOverlay" onClick={closeManage}>
          <form className="gwModal" onClick={(e) => e.stopPropagation()} onSubmit={saveCredentials} style={{ width: 480 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Device Credentials</div>
                <div className="gwModalSubtitle">Update the hosted broker login used by MQTT devices.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={closeManage} disabled={saving}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <div style={{ padding: 10, borderRadius: 6, border: "1px solid rgba(255,193,7,0.35)", background: "rgba(255,193,7,0.08)", color: "var(--gw-text-primary)", fontSize: 12, lineHeight: 1.45 }}>
                Changing these credentials disconnects devices that still use the old password. Update field devices after saving.
              </div>
              <div className="gwField" style={{ marginTop: 12 }}>
                <label>Username</label>
                <input
                  className="gwInput"
                  value={formUsername}
                  onChange={(e) => setFormUsername(e.target.value)}
                  autoComplete="off"
                  required
                  autoFocus
                />
              </div>
              <div className="gwField" style={{ marginTop: 12 }}>
                <label>Password</label>
                <input
                  className="gwInput"
                  type="password"
                  value={formPassword}
                  onChange={(e) => setFormPassword(e.target.value)}
                  placeholder={rotatePassword ? "Generated on save" : "Leave blank to keep current password"}
                  disabled={rotatePassword}
                  autoComplete="new-password"
                />
              </div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 12, fontSize: 13, color: "var(--gw-text-secondary)", lineHeight: 1.4 }}>
                <input
                  type="checkbox"
                  checked={rotatePassword}
                  onChange={(e) => {
                    setRotatePassword(e.target.checked);
                    if (e.target.checked) setFormPassword("");
                  }}
                  style={{ marginTop: 2 }}
                />
                <span>Generate a new password instead of entering one manually.</span>
              </label>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 12, fontSize: 13, color: "var(--gw-text-secondary)", lineHeight: 1.4 }}>
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  required
                  style={{ marginTop: 2 }}
                />
                <span>I understand devices using the old password must be updated.</span>
              </label>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={closeManage} disabled={saving}>Cancel</button>
              <button className="gwPrimaryBtn" type="submit" disabled={saving || !acknowledged}>
                {saving ? "Saving..." : rotatePassword ? "Rotate Password" : "Save Credentials"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export const MqttBrokersPage = () => {
  const canConfigure = usePermission("configure");
  const [brokers, setBrokers] = useState([]);
  const [states, setStates] = useState({}); // id → BrokerState
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [proposed, setProposed] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuStyle, setMenuStyle] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [provisioningHosted, setProvisioningHosted] = useState(false);
  const notify = useNotify();

  // List brokers + their live states. State endpoint is broker-scoped, so
  // we fan it out with Promise.allSettled to keep one slow broker from
  // blocking the list-render.
  const fetchAll = useCallback(async () => {
    try {
      const list = (await listMqttBrokers()) || [];
      setBrokers(list);
      const stateResults = await Promise.allSettled(
        list.map((b) => getMqttBrokerState(b.id).then((s) => [b.id, s]))
      );
      const next = {};
      for (const r of stateResults) {
        if (r.status === "fulfilled") {
          const [id, st] = r.value;
          next[id] = st;
        }
      }
      setStates(next);
    } catch (err) {
      notify({ type: "error", title: "Error", message: errorMessage(err, "Failed to load brokers") });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchAll]);

  // Refresh when the AI mutates a broker or subscription.
  useEffect(() => {
    const handler = (e) => {
      const r = e.detail?.resource;
      if (r === "mqtt_brokers" || r === "mqtt_subscriptions") fetchAll();
    };
    window.addEventListener("gw-ai-resource-changed", handler);
    return () => window.removeEventListener("gw-ai-resource-changed", handler);
  }, [fetchAll]);

  // AI propose-mqtt-broker: open the New Broker modal pre-filled.
  useEffect(() => {
    const handler = (e) => {
      const payload = e.detail?.payload;
      if (!payload || typeof payload !== "object") return;
      setProposed(payload);
      setEditing(null);
      setShowCreate(true);
    };
    window.addEventListener("gw-ai-propose-mqtt-broker-page", handler);
    return () => window.removeEventListener("gw-ai-propose-mqtt-broker-page", handler);
  }, []);

  const openMenu = (e, id) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenuStyle({ position: "fixed", top: `${Math.round(r.bottom + 4)}px`, right: `${Math.max(12, Math.round(window.innerWidth - r.right))}px` });
    setMenuOpen(menuOpen === id ? null : id);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      const menu = document.querySelector(".gwProjectMenu");
      if (menu?.contains(e.target)) return;
      setMenuOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteMqttBroker(deleteTarget.id);
      notify({ type: "success", title: "Removed", message: `Broker "${deleteTarget.name || deleteTarget.id}" deleted.` });
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      notify({ type: "error", title: "Delete Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setDeleting(false);
    }
  };

  const hasBrokers = brokers.length > 0;
  const hostedBroker = brokers.find((b) => b.hosted);

  const provisionHosted = async () => {
    setProvisioningHosted(true);
    try {
      await provisionHostedMqttBroker();
      notify({ type: "success", title: "Broker Provisioned", message: "Hosted MQTT broker is ready for devices." });
      fetchAll();
    } catch (err) {
      notify({ type: "error", title: "Provision Failed", message: errorMessage(err, "Could not provision hosted broker") });
    } finally {
      setProvisioningHosted(false);
    }
  };

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">MQTT</div>
            <div className="gwPageSubtitle">
              {hasBrokers ? `${brokers.length} broker${brokers.length !== 1 ? "s" : ""} configured` : "Configure MQTT brokers for push-driven tag ingestion."}
            </div>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {!hostedBroker && (
              <button
                className="gwGhostBtn"
                type="button"
                onClick={provisionHosted}
                disabled={!canConfigure || provisioningHosted}
                title={canConfigure ? undefined : "Requires configure permission"}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
                <span>{provisioningHosted ? "Provisioning..." : "Provision Hosted Broker"}</span>
              </button>
            )}
            <button
              className="gwPrimaryBtn"
              type="button"
              onClick={() => setShowCreate(true)}
              disabled={!canConfigure}
              title={canConfigure ? undefined : "Requires configure permission"}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
              <span>Add Broker</span>
            </button>
          </div>
        </div>

        {loading && !hasBrokers && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>Loading brokers...</div>
        )}

        {!loading && !hasBrokers && (
          <div className="gwEmptyState">
            <div className="gwEmptyTitle">No brokers yet</div>
            <div className="gwEmptyBody">Add an MQTT broker to start ingesting tag values from devices and gateways.</div>
            <div className="gwEmptyActions">
              <button
                className="gwGhostBtn"
                type="button"
                onClick={provisionHosted}
                disabled={!canConfigure || provisioningHosted}
                title={canConfigure ? undefined : "Requires configure permission"}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
                <span>{provisioningHosted ? "Provisioning..." : "Provision Hosted Broker"}</span>
              </button>
              <button
                className="gwPrimaryBtn"
                type="button"
                onClick={() => setShowCreate(true)}
                disabled={!canConfigure}
                title={canConfigure ? undefined : "Requires configure permission"}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
                <span>Add Broker</span>
              </button>
            </div>
          </div>
        )}

        {hasBrokers && (
          <div className="gwBrokerSection">
            <div className="gwSectionTitle">Brokers</div>
            <div className="gwBrokerList">
              {[...brokers]
                .sort((a, b) => (b.hosted ? 1 : 0) - (a.hosted ? 1 : 0))
                .map((b) => {
                const st = states[b.id];
                const status = st?.status || "offline";
                const colors = STATUS_COLORS[status] || STATUS_COLORS.offline;
                const managedTitle = "Managed by Control Seat — cannot be edited or removed";
                return (
                  <div
                    key={b.id}
                    className="gwBrokerCard"
                    data-gw-resource="mqtt_brokers"
                    data-gw-id={b.id}
                    data-gw-name={b.name}
                  >
                    <div className="gwBrokerHeader">
                      <div
                        style={{
                          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                          background: colors.bg,
                          border: `1px solid ${colors.border}`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 700, color: colors.color,
                        }}
                      >
                        MQ
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span className="umUserName">{b.name || b.id}</span>
                          <StatusDot status={status} />
                          {b.hosted && (
                            <span
                              title="This broker is hosted and managed by Control Seat"
                              style={{
                                fontSize: 11, fontWeight: 600, padding: "1px 8px", borderRadius: 999,
                                background: "var(--gw-accent-soft, rgba(80,120,255,0.12))",
                                color: "var(--gw-accent, #5078ff)",
                                border: "1px solid var(--gw-accent-border, rgba(80,120,255,0.3))",
                              }}
                            >
                              Managed by Control Seat
                            </span>
                          )}
                        </div>
                        <div className="umUserMeta">
                          {b.hosted ? (b.public_url || "Hosted broker") : b.url}
                          {b.has_password ? " · auth" : ""}
                          {b.tls_enabled ? " · tls" : ""}
                          {st?.last_error_msg && (
                            <span style={{ color: "var(--gw-danger)" }} title={st.last_error_msg}> &middot; {st.last_error_msg}</span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="gwIconButton"
                        style={{ flexShrink: 0 }}
                        onClick={(e) => openMenu(e, b.id)}
                      >
                        <img src={moreIcon} alt="More" className="gwIcon" style={{ width: 16, height: 16 }} />
                      </button>
                      {menuOpen === b.id && (
                        <div className="gwProjectMenu" style={menuStyle} onClick={(e) => e.stopPropagation()}>
                          <button
                            className="gwProjectMenuItem"
                            type="button"
                            disabled={!canConfigure || b.hosted}
                            title={b.hosted ? managedTitle : (canConfigure ? undefined : "Requires configure permission")}
                            onClick={() => { setMenuOpen(null); setEditing(b); }}
                          >
                            Edit
                          </button>
                          <div className="gwProfileDivider" />
                          <button
                            className="gwProjectMenuItem gwProjectMenuDanger"
                            type="button"
                            disabled={!canConfigure || b.hosted}
                            title={b.hosted ? managedTitle : (canConfigure ? undefined : "Requires configure permission")}
                            onClick={() => { setMenuOpen(null); setDeleteTarget(b); }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                    {b.hosted && <HostedConnectionPanel broker={b} canConfigure={canConfigure} onUpdated={fetchAll} />}
                    <SubscriptionsPanel brokerId={b.id} canConfigure={canConfigure} />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {showCreate && (
        <BrokerModal
          prefilled={proposed}
          onClose={() => { setShowCreate(false); setProposed(null); }}
          onSaved={fetchAll}
        />
      )}
      {editing && <BrokerModal editing={editing} onClose={() => setEditing(null)} onSaved={fetchAll} />}

      {deleteTarget && (
        <div className="gwOverlay" onClick={() => setDeleteTarget(null)}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 420 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Remove Broker</div>
                <div className="gwModalSubtitle">Subscriptions and auto-owned tags will cascade.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setDeleteTarget(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                Remove broker <strong>{deleteTarget.name || deleteTarget.id}</strong>? Any tags bound to this broker via direct sources will go QualityBad.
              </p>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="gwDangerBtn" type="button" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Removing..." : "Remove Broker"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
